import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { createPageUrl, getCsrfToken } from '@/utils';
import { TooltipButton } from '@/components/ui/TooltipButton';
import GenreSelector from '@/components/GenreSelector';
import batMoonIcon from '@/assets/bat-moon.png';
import { hapticMedium, hapticSuccess, hapticError } from '@/utils/haptic';
import { supabase } from '@/integrations/supabase/client';
import { usePlayer } from '@/contexts/PlayerContext';
import { Progress } from '@/components/ui/progress';
import { Coins, AlertCircle, Plus, Trash2, Music, Upload as UploadIcon, Image, Check, Loader2 } from 'lucide-react';
import { compressImage, needsCompression } from '@/utils/imageCompression';

const emptyTrack = () => ({
  id: crypto.randomUUID(),
  title: '',
  description: '',
  songStory: '',
  lyrics: '',
  artistName: '',
  audio: null,
  cover: null,
  selectedGenres: [],
  uploadProgress: 0,
  uploadStatus: 'pending', // pending, uploading, success, error
  errorMessage: '',
});

export default function Upload() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clearFailedUrls } = usePlayer();
  const [currentUser, setCurrentUser] = useState(null);
  const [tracks, setTracks] = useState([emptyTrack()]);
  const [loading, setLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [existingTrackCount, setExistingTrackCount] = useState(0);
  const [loadingTrackCount, setLoadingTrackCount] = useState(true);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('Session fetch error:', error);
      }

      if (!session || !session.user) {
        setCurrentUser(null);
        setAuthChecked(true);
        setLoadingTrackCount(false);
        return;
      }

      // Get profile from Supabase
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .maybeSingle();
        
      const userProfile = profile || {
        id: session.user.id,
        email: session.user.email,
        username: session.user.user_metadata?.username || session.user.email,
        premium_credits: 0
      };

      setCurrentUser(userProfile);
      
      // Fetch track count for this user from Supabase
      try {
        const { data: userTracks, error: tracksError } = await supabase
          .from("tracks")
          .select("id")
          .eq("artist_id", session.user.id);
          
        if (!tracksError) {
          setExistingTrackCount(userTracks?.length || 0);
        }
      } catch (err) {
        console.error('Error fetching track count:', err);
      }
      setLoadingTrackCount(false);
    } catch (error) {
      console.error('Error loading user:', error);
      setCurrentUser(null);
      setLoadingTrackCount(false);
    } finally {
      setAuthChecked(true);
    }
  };


  // Calculate costs: 1 credit = 1 upload
  // New users start with 1 credit on signup
  const UPLOAD_COST_CREDITS = 1; // 1 credit per upload
  
  const calculateCosts = () => {
    const userCredits = currentUser?.premium_credits || 0;
    const totalCostCredits = tracks.length * UPLOAD_COST_CREDITS;

    return {
      totalCostCredits,
      hasEnoughCredits: userCredits >= totalCostCredits,
      userCredits,
      creditsNeeded: Math.max(0, totalCostCredits - userCredits),
    };
  };

  const costs = calculateCosts();

  const updateTrack = (id, field, value) => {
    setTracks(prev => prev.map(t => t.id === id ? { ...t, [field]: value } : t));
  };

  const updateTrackProgress = (id, progress, status, errorMessage = '') => {
    setTracks(prev => prev.map(t => 
      t.id === id ? { ...t, uploadProgress: progress, uploadStatus: status, errorMessage } : t
    ));
  };

  const addTrack = () => {
    setTracks(prev => [...prev, emptyTrack()]);
  };

  const removeTrack = (id) => {
    if (tracks.length <= 1) return;
    setTracks(prev => prev.filter(t => t.id !== id));
  };

  const validateAudioFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };
    
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      return { valid: false, error: 'Audio file is too large. Maximum size is 100MB.' };
    }

    const validTypes = [
      'audio/mpeg', 'audio/mp3', 'audio/x-mpeg', 'audio/x-mpeg-3', 'audio/mpeg3',
      'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/vnd.wave', 'audio/x-pn-wav',
      'audio/mp4', 'audio/x-m4a', 'audio/m4a', 'audio/aac', 'audio/x-aac',
      'audio/ogg', 'audio/vorbis', 'audio/x-ogg', 'application/ogg',
      'audio/flac', 'audio/x-flac', 'audio/vnd.flac'
    ];
    const validExtension = /\.(mp3|wav|m4a|ogg|flac)$/i;
    const hasValidExtension = file.name.match(validExtension);
    const hasValidMimeType = validTypes.includes(file.type);
    
    if (!hasValidExtension && !hasValidMimeType) {
      return { valid: false, error: 'Invalid audio file type. Please upload MP3, WAV, M4A, OGG, or FLAC files.' };
    }

    return { valid: true };
  };

  const validateCoverFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };
    
    const maxSize = 50 * 1024 * 1024; // 50MB limit for cover art
    if (file.size > maxSize) {
      return { valid: false, error: 'Cover image is too large. Maximum size is 50MB.' };
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp)$/i)) {
      return { valid: false, error: 'Invalid image file type. Please upload JPG, PNG, or WEBP files.' };
    }

    return { valid: true };
  };

  // Upload file to Supabase storage with real progress tracking
  const uploadToSupabaseStorage = async (bucket, file, userId, onProgress) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    
    onProgress(1);
    
    // Use XMLHttpRequest for real upload progress
    const { data: { session } } = await supabase.auth.getSession();
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const uploadUrl = `${supabaseUrl}/storage/v1/object/${bucket}/${fileName}`;
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 95) + 1;
          onProgress(Math.min(percentComplete, 96));
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(100);
          // Get public URL
          const { data: urlData } = supabase.storage
            .from(bucket)
            .getPublicUrl(fileName);
          resolve(urlData.publicUrl);
        } else {
          let errorMessage = 'Upload failed';
          try {
            const response = JSON.parse(xhr.responseText);
            errorMessage = response.message || response.error || errorMessage;
          } catch (e) {}
          reject(new Error(`Failed to upload to ${bucket}: ${errorMessage}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        reject(new Error(`Failed to upload to ${bucket}: Network error`));
      });
      
      xhr.addEventListener('abort', () => {
        reject(new Error(`Upload to ${bucket} was aborted`));
      });
      
      xhr.open('POST', uploadUrl);
      xhr.setRequestHeader('Authorization', `Bearer ${session?.access_token || supabaseKey}`);
      xhr.setRequestHeader('apikey', supabaseKey);
      xhr.setRequestHeader('x-upsert', 'false');
      xhr.setRequestHeader('Cache-Control', '3600');
      xhr.send(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      hapticError();
      alert('Please sign in to upload tracks');
      return;
    }

    // Validate all tracks
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      const trackNum = i + 1;

      if (!track.audio) {
        hapticError();
        alert(`Track ${trackNum}: Audio file is required`);
        return;
      }

      if (!track.cover) {
        hapticError();
        alert(`Track ${trackNum}: Cover image is required`);
        return;
      }

      if (track.selectedGenres.length === 0) {
        hapticError();
        alert(`Track ${trackNum}: At least one genre is required`);
        return;
      }

      if (!track.artistName.trim()) {
        hapticError();
        alert(`Track ${trackNum}: Artist name is required`);
        return;
      }

      if (!track.title.trim()) {
        hapticError();
        alert(`Track ${trackNum}: Track title is required`);
        return;
      }
    }

    if (!costs.hasEnoughCredits) {
      hapticError();
      alert(`You need ${costs.totalCostCredits} credit(s) to upload ${tracks.length} track(s). You have ${costs.userCredits}.`);
      return;
    }

    hapticMedium();
    setLoading(true);

    // Reset all track statuses
    tracks.forEach(track => {
      updateTrackProgress(track.id, 0, 'pending');
    });

    let successCount = 0;
    const errors = [];
    
    try {
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];

        updateTrackProgress(track.id, 0, 'uploading');

        try {
          // Upload audio file to Supabase storage (0-45% progress)
          const audioUrl = await uploadToSupabaseStorage(
            'tracks',
            track.audio,
            currentUser.id,
            (progress) => updateTrackProgress(track.id, Math.round(progress * 0.45), 'uploading')
          );

          // Upload cover image to Supabase storage (45-90% progress)
          const coverUrl = await uploadToSupabaseStorage(
            'covers',
            track.cover,
            currentUser.id,
            (progress) => updateTrackProgress(track.id, 45 + Math.round(progress * 0.45), 'uploading')
          );

          // Create track record in Supabase (90-100%)
          updateTrackProgress(track.id, 90, 'uploading');

          // Deduct 1 credit for this upload
          const { error: creditError } = await supabase
            .from("profiles")
            .update({ 
              premium_credits: (currentUser.premium_credits || 0) - 1
            })
            .eq("id", currentUser.id);
          
          if (creditError) {
            throw new Error('Failed to deduct credits: ' + creditError.message);
          }

          // Insert track into database
          const { error: trackError } = await supabase
            .from("tracks")
            .insert({
              title: track.title,
              description: track.description || null,
              artist_name: track.artistName.trim(),
              artist_id: currentUser.id,
              audio_url: audioUrl,
              cover_image_url: coverUrl,
              genres: track.selectedGenres,
              is_active: true,
              praise_count: 0,
              neutral_count: 0,
              scathe_count: 0,
              boost_credits: 0,
              total_listens: 0,
              completed_listens: 0
            });

          if (trackError) {
            throw new Error('Failed to create track: ' + trackError.message);
          }

          updateTrackProgress(track.id, 100, 'success');
          successCount++;
        } catch (err) {
          console.error(`Track ${trackNum} upload error:`, err);
          updateTrackProgress(track.id, 0, 'error', err.message);
          errors.push(`Track ${trackNum} (${track.title || 'Untitled'}): ${err.message}`);
        }
      }

      if (successCount > 0) {
        hapticSuccess();
        queryClient.invalidateQueries({ queryKey: ['discover-tracks'] });
        clearFailedUrls();
        
        if (errors.length > 0) {
          alert(`${successCount} track(s) uploaded successfully!\n\nFailed uploads:\n${errors.join('\n')}`);
        } else {
          alert(`${successCount} track(s) uploaded successfully!`);
        }
        navigate(createPageUrl('MyTracks'));
      } else {
        hapticError();
        alert(`All uploads failed:\n${errors.join('\n')}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      hapticError();
      alert('Failed to upload tracks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-8 pb-32 max-w-full overflow-x-hidden">
      <div className="max-w-2xl mx-auto w-full">
        <div className="neuro-base rounded-3xl p-4 md:p-8 w-full">
          <div className="flex items-center gap-2 md:gap-4 mb-6 md:mb-8 flex-wrap">
            <div className="neuro-pressed w-16 h-16 md:w-24 md:h-24 rounded-2xl flex items-center justify-center overflow-hidden bg-[#0a0a0a] p-2 flex-shrink-0">
              <img 
                src={batMoonIcon} 
                alt="Bat icon" 
                className="w-full h-full object-contain"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl md:text-3xl font-light text-[#d0d0d0] break-words">Upload Tracks</h1>
              <p className="text-xs md:text-sm text-[#808080] break-words">Drag & drop or select files</p>
            </div>
          </div>

          {/* Cost Summary Banner */}
          {authChecked && currentUser && !loadingTrackCount && (
            <div className={`mb-6 p-4 rounded-2xl border ${
              costs.hasEnoughCredits 
                ? 'bg-[#1a1a1a] border-[#2a2a2a]' 
                : 'bg-red-900/20 border-red-700/50'
            }`}>
              {costs.hasEnoughCredits ? (
                <div className="flex items-center gap-3">
                  <Coins className="w-5 h-5 text-[#a0a0a0] flex-shrink-0" />
                  <div>
                    <p className="text-[#c0c0c0]">
                      <span className="font-semibold text-[#e0e0e0]">{tracks.length} track(s)</span>
                      <span> = {costs.totalCostCredits} credit(s)</span>
                    </p>
                    <p className="text-xs text-[#808080]">You have {costs.userCredits} credit(s)</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <div>
                      <p className="text-red-400 font-medium">Not enough credits</p>
                      <p className="text-xs text-[#808080]">
                        You need {costs.creditsNeeded} more credit(s). You have {costs.userCredits}.
                      </p>
                    </div>
                  </div>
                  <Link to="/Shop" className="text-sm text-[#7f5af0] hover:underline whitespace-nowrap">
                    Buy credits →
                  </Link>
                </div>
              )}
            </div>
          )}

          {!authChecked && (
            <div className="mb-6 p-4 rounded-2xl bg-[#1a1a1a] border border-[#2a2a2a] text-center text-sm text-[#a0a0a0]">
              Checking your session...
            </div>
          )}

          {authChecked && !currentUser && (
            <div className="mb-6 p-4 rounded-2xl bg-[#1a1a1a] border border-[#2a2a2a]">
              <p className="text-sm md:text-base text-[#a0a0a0] text-center">
                Please <Link to={createPageUrl("Auth")} className="text-blue-400 hover:text-blue-300 underline">sign in</Link> to upload tracks.
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {tracks.map((track, index) => (
              <TrackUploadCard
                key={track.id}
                track={track}
                index={index}
                totalTracks={tracks.length}
                isFree={false}
                disabled={!currentUser || loading}
                onUpdate={(field, value) => updateTrack(track.id, field, value)}
                onRemove={() => removeTrack(track.id)}
                validateAudioFile={validateAudioFile}
                validateCoverFile={validateCoverFile}
              />
            ))}

            {/* Add Another Track Button */}
            <button
              type="button"
              onClick={addTrack}
              disabled={!currentUser || loading}
              className="w-full py-3 neuro-pressed rounded-2xl flex items-center justify-center gap-2 text-[#808080] hover:text-[#a0a0a0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
              <span>Add Another Track</span>
            </button>

            <TooltipButton
              type="submit"
              disabled={loading || !currentUser || !costs.hasEnoughCredits}
              className="w-full py-3 md:py-4 neuro-base active:neuro-pressed rounded-2xl smooth-transition disabled:opacity-50"
              tooltip={!currentUser ? "Please sign in to upload tracks" : !costs.hasEnoughCredits ? "You need more credits to upload. Buy credits in the Shop." : loading ? "Uploading your tracks..." : `Upload ${tracks.length} track(s)`}
            >
              <span className="text-[#a0a0a0] text-base md:text-lg">
                {loading 
                  ? `Uploading ${tracks.length} track(s)...` 
                  : `Upload ${tracks.length} Track(s) (${costs.totalCostCredits} credit${costs.totalCostCredits !== 1 ? 's' : ''})`
                }
              </span>
            </TooltipButton>
          </form>
        </div>
      </div>
    </div>
  );
}

function TrackUploadCard({ track, index, totalTracks, isFree, disabled, onUpdate, onRemove, validateAudioFile, validateCoverFile }) {
  const audioInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [audioDragActive, setAudioDragActive] = useState(false);
  const [coverDragActive, setCoverDragActive] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleAudioChange = (file) => {
    if (!file) {
      onUpdate('audio', null);
      return;
    }
    const result = validateAudioFile(file);
    if (!result.valid) {
      alert(result.error);
      onUpdate('audio', null);
      return;
    }
    onUpdate('audio', file);
  };

  const handleCoverChange = async (file) => {
    if (!file) {
      onUpdate('cover', null);
      return;
    }
    const result = validateCoverFile(file);
    if (!result.valid) {
      alert(result.error);
      onUpdate('cover', null);
      return;
    }
    
    // Compress image if it's larger than 1MB
    if (needsCompression(file, 1024)) {
      setIsCompressing(true);
      try {
        const compressedFile = await compressImage(file, {
          maxWidth: 2000,
          maxHeight: 2000,
          quality: 0.85,
          maxSizeKB: 2048 // Target 2MB max
        });
        onUpdate('cover', compressedFile);
      } catch (err) {
        console.error('Compression failed, using original:', err);
        onUpdate('cover', file);
      } finally {
        setIsCompressing(false);
      }
    } else {
      onUpdate('cover', file);
    }
  };

  const handleDrag = useCallback((e, setActive) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setActive(true);
    } else if (e.type === 'dragleave') {
      setActive(false);
    }
  }, []);

  const handleAudioDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setAudioDragActive(false);
    if (disabled) return;
    
    const file = e.dataTransfer.files?.[0];
    if (file) handleAudioChange(file);
  }, [disabled]);

  const handleCoverDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setCoverDragActive(false);
    if (disabled) return;
    
    const file = e.dataTransfer.files?.[0];
    if (file) handleCoverChange(file);
  }, [disabled]);

  const progressColor = track.uploadStatus === 'error' ? 'bg-red-500' : 
                         track.uploadStatus === 'success' ? 'bg-green-500' : 
                         'bg-[#7f5af0]';

  return (
    <div className={`neuro-pressed rounded-2xl p-4 md:p-5 border space-y-4 ${
      track.uploadStatus === 'error' ? 'border-red-700/50' : 
      track.uploadStatus === 'success' ? 'border-green-700/50' : 
      'border-[#1b1b1b]'
    }`}>
      {/* Track Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Music className="w-5 h-5 text-[#808080]" />
          <span className="text-[#a0a0a0] font-medium">Track {index + 1}</span>
          {isFree && (
            <span className="text-xs bg-green-900/30 text-green-400 px-2 py-0.5 rounded-full">FREE</span>
          )}
          {!isFree && (
            <span className="text-xs bg-[#2a2a2a] text-[#808080] px-2 py-0.5 rounded-full">1 credit</span>
          )}
        </div>
        {totalTracks > 1 && !disabled && (
          <button
            type="button"
            onClick={onRemove}
            className="p-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Upload Progress Bar */}
      {(track.uploadStatus === 'uploading' || track.uploadStatus === 'success' || track.uploadStatus === 'error') && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className={
              track.uploadStatus === 'error' ? 'text-red-400' : 
              track.uploadStatus === 'success' ? 'text-green-400' : 
              'text-[#808080]'
            }>
              {track.uploadStatus === 'error' ? 'Failed' : 
               track.uploadStatus === 'success' ? 'Complete' : 
               'Uploading...'}
            </span>
            <span className="text-[#808080]">{track.uploadProgress}%</span>
          </div>
          <div className="w-full h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 rounded-full ${progressColor}`}
              style={{ width: `${track.uploadProgress}%` }}
            />
          </div>
          {track.errorMessage && (
            <p className="text-xs text-red-400 mt-1">{track.errorMessage}</p>
          )}
        </div>
      )}

      {/* Artist Name */}
      <div>
        <label className="block text-xs text-[#808080] mb-1">Artist Name *</label>
        <input
          type="text"
          placeholder="Enter artist or stage name"
          value={track.artistName}
          onChange={(e) => onUpdate('artistName', e.target.value)}
          disabled={disabled}
          className="w-full p-3 neuro-pressed rounded-xl bg-transparent text-[#d0d0d0] placeholder:text-[#606060] focus:outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          required
        />
      </div>

      {/* Track Title */}
      <div>
        <label className="block text-xs text-[#808080] mb-1">Track Title *</label>
        <input
          type="text"
          placeholder="Enter track title"
          value={track.title}
          onChange={(e) => onUpdate('title', e.target.value)}
          disabled={disabled}
          className="w-full p-3 neuro-pressed rounded-xl bg-transparent text-[#d0d0d0] placeholder:text-[#606060] focus:outline-none text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          required
        />
      </div>

      {/* Audio & Cover Drag-and-Drop Areas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Audio Drop Zone */}
        <div
          onDragEnter={(e) => handleDrag(e, setAudioDragActive)}
          onDragLeave={(e) => handleDrag(e, setAudioDragActive)}
          onDragOver={(e) => handleDrag(e, setAudioDragActive)}
          onDrop={handleAudioDrop}
          onClick={() => !disabled && audioInputRef.current?.click()}
          className={`relative cursor-pointer p-4 rounded-xl border-2 border-dashed transition-all ${
            disabled ? 'opacity-50 cursor-not-allowed' :
            audioDragActive ? 'border-[#7f5af0] bg-[#7f5af0]/10' :
            track.audio ? 'border-green-600 bg-green-900/10' :
            'border-[#3a3a3a] hover:border-[#5a5a5a]'
          }`}
        >
          <input
            ref={audioInputRef}
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac"
            disabled={disabled}
            onChange={(e) => handleAudioChange(e.target.files?.[0])}
            className="hidden"
          />
          <div className="flex flex-col items-center gap-2 text-center">
            {track.audio ? (
              <>
                <Check className="w-8 h-8 text-green-400" />
                <p className="text-xs text-green-400 truncate max-w-full">{track.audio.name}</p>
              </>
            ) : (
              <>
                <UploadIcon className="w-8 h-8 text-[#606060]" />
                <p className="text-xs text-[#808080]">
                  Drop audio or <span className="text-[#7f5af0]">browse</span>
                </p>
                <p className="text-[10px] text-[#606060]">MP3, WAV, M4A, OGG, FLAC</p>
              </>
            )}
          </div>
        </div>

        {/* Cover Drop Zone */}
        <div
          onDragEnter={(e) => handleDrag(e, setCoverDragActive)}
          onDragLeave={(e) => handleDrag(e, setCoverDragActive)}
          onDragOver={(e) => handleDrag(e, setCoverDragActive)}
          onDrop={handleCoverDrop}
          onClick={() => !disabled && !isCompressing && coverInputRef.current?.click()}
          className={`relative cursor-pointer p-4 rounded-xl border-2 border-dashed transition-all ${
            disabled || isCompressing ? 'opacity-50 cursor-not-allowed' :
            coverDragActive ? 'border-[#7f5af0] bg-[#7f5af0]/10' :
            track.cover ? 'border-green-600 bg-green-900/10' :
            'border-[#3a3a3a] hover:border-[#5a5a5a]'
          }`}
        >
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*,.jpg,.jpeg,.png,.webp"
            disabled={disabled || isCompressing}
            onChange={(e) => handleCoverChange(e.target.files?.[0])}
            className="hidden"
          />
          <div className="flex flex-col items-center gap-2 text-center">
            {isCompressing ? (
              <>
                <Loader2 className="w-8 h-8 text-[#7f5af0] animate-spin" />
                <p className="text-xs text-[#7f5af0]">Compressing image...</p>
              </>
            ) : track.cover ? (
              <>
                <Check className="w-8 h-8 text-green-400" />
                <p className="text-xs text-green-400 truncate max-w-full">{track.cover.name}</p>
                <p className="text-[10px] text-[#808080]">
                  {(track.cover.size / 1024).toFixed(0)}KB
                </p>
              </>
            ) : (
              <>
                <Image className="w-8 h-8 text-[#606060]" />
                <p className="text-xs text-[#808080]">
                  Drop cover or <span className="text-[#7f5af0]">browse</span>
                </p>
                <p className="text-[10px] text-[#606060]">JPG, PNG, WEBP (auto-compressed)</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Genres */}
      <div>
        <label className="block text-xs text-[#808080] mb-1">Genres *</label>
        <div className="w-full overflow-x-hidden">
          <GenreSelector 
            selectedGenres={track.selectedGenres}
            onChange={(genres) => onUpdate('selectedGenres', genres)}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Optional Fields - Collapsible */}
      <details className="group">
        <summary className="cursor-pointer text-xs text-[#707070] hover:text-[#909090] transition-colors">
          Optional details (description, story, lyrics)
        </summary>
        <div className="mt-3 space-y-3">
          <div>
            <label className="block text-xs text-[#808080] mb-1">Description</label>
            <textarea
              placeholder="Describe your track"
              value={track.description}
              onChange={(e) => onUpdate('description', e.target.value)}
              disabled={disabled}
              className="w-full p-3 neuro-pressed rounded-xl bg-transparent text-[#d0d0d0] placeholder:text-[#606060] focus:outline-none resize-none text-sm disabled:opacity-50"
              rows={2}
            />
          </div>
          <div>
            <label className="block text-xs text-[#808080] mb-1">Song Story</label>
            <textarea
              placeholder="Share the story behind this song..."
              value={track.songStory}
              onChange={(e) => onUpdate('songStory', e.target.value)}
              disabled={disabled}
              className="w-full p-3 neuro-pressed rounded-xl bg-transparent text-[#d0d0d0] placeholder:text-[#606060] focus:outline-none resize-none text-sm disabled:opacity-50"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-xs text-[#808080] mb-1">Lyrics</label>
            <textarea
              placeholder="Paste your song lyrics here..."
              value={track.lyrics}
              onChange={(e) => onUpdate('lyrics', e.target.value)}
              disabled={disabled}
              className="w-full p-3 neuro-pressed rounded-xl bg-transparent text-[#d0d0d0] placeholder:text-[#606060] focus:outline-none resize-none text-sm font-mono disabled:opacity-50"
              rows={5}
            />
          </div>
        </div>
      </details>
    </div>
  );
}
